import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, FieldArray } from 'redux-form';
import { find, propEq, prop } from 'ramda';
import ReactMDE from 'redux-forms-markdown-editor';

import { required, url } from '../utilities/validation';
import { renderField, renderFileField, renderArray } from '../utilities/form';
import { toInt } from '../utilities/normalisation';

import BggModal from './BggModal';
import { storage } from '../firebase';
import { range } from '../utilities';
import { v4 as uuidv4 } from 'uuid';

const BoardgameForm = ({ title, handleSubmit, invalid, submitting, change, ...props }) => {
  const [ isBggModalOpen, setBggModalOpen ] = useState(false);
  const [ minPlayersOptions, setMinPlayersOptions ] = useState(range(1, 6));
  const [ maxPlayersOptions, setMaxPlayersOptions ] = useState(range(1, 12));
  const [ minPlayTimeOptions, setMinPlayTimeOptions ] = useState(range(1, 9).concat(range(10, 60, 5)));
  const [ maxPlayTimeOptions, setMaxPlayTimeOptions ] = useState(range(1, 9).concat(range(10, 90, 5)));
  const [ isUploading, setUploading ] = useState(false);

  const availableCopiesOptions = range(1, 10);

  const insertMissingOption = (options, value) => {
    const index = find(x => x > value, options);
    return [
      ...options.slice(0, index ? index : options.length),
      value,
      ...options.slice(index ? index : options.length)
    ];
  };

  const onBggGameSelected = (game) => {
    setBggModalOpen(false);

    const names = Array.isArray(game.name) ? game.name : [game.name];
    const bggId = prop('id', game);
    const title = prop('value', find(propEq('type', 'primary'))(names));
    const minPlayers = prop('value', game.minPlayers);
    const maxPlayers = prop('value', game.maxPlayers);
    const minPlayTime = prop('value', game.minPlayTime);
    const maxPlayTime = prop('value', game.maxPlayTime);
    const image = prop('image', game);
    const thumbnail = prop('thumbnail', game);

    if (minPlayersOptions.indexOf(minPlayers) < 0) {
      setMinPlayersOptions(insertMissingOption(minPlayersOptions, minPlayers));
    }

    if (maxPlayersOptions.indexOf(maxPlayers) < 0) {
      setMaxPlayersOptions(insertMissingOption(maxPlayersOptions, maxPlayers));
    }

    if (minPlayTimeOptions.indexOf(minPlayTime) < 0) {
      setMinPlayTimeOptions(insertMissingOption(minPlayTimeOptions, minPlayTime));
    }

    if (maxPlayTimeOptions.indexOf(maxPlayTime) < 0) {
      setMaxPlayTimeOptions(insertMissingOption(maxPlayTimeOptions, maxPlayTime));
    }

    change('bggId', bggId);
    change('title', title);
    change('minPlayers', minPlayers);
    change('maxPlayers', minPlayers !== maxPlayers ? maxPlayers : null);
    change('minPlayTime', minPlayTime);
    change('maxPlayTime', minPlayTime !== maxPlayTime ? maxPlayTime : null);
    change('image', image);
    change('thumbnail', thumbnail);
  };

  const handleFile = (field) => {
    return (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        setUploading(true);
        const file = files[0];
        const ext = file.name.split('.').pop();
        const imageRef = storage.ref().child(`images/${uuidv4()}.${ext}`);
        imageRef.put(file)
          .then(snapshot => snapshot.ref.getDownloadURL())
          .then(url => {
            change(field, url);
            setUploading(false);
          });
      }
    };
  };

  const mdConfig = {
    bold: true,
    italic: true,
    heading: true,
    orderedList: true,
    unorderedList: true,
    blockQuote: false,
    html: false,
    url: true,
    image: true,
    youtube: true,
    canPreview: false,
  };

  const mdButtonStyle = {
    borderRadius: '4px',    
  };

  const mdTextAreaStyle = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundColor: '#edf2f7',
    border: '#edf2f7',
    padding: '.75rem 1rem .75rem 1rem',
    borderRadius: '.25rem',
  };

  return (
    <React.Fragment>
      <div className="flex flex-wrap justify-center">
        <div className="w-full max-w-lg relative">
          <h1 className="font-black font-serif mb-8 text-3xl tracking-tight block text-black">{ title }</h1>
          {
            !props.editMode 
              ? <button className="bg-blue-700 hover:bg-blue-900 absolute text-white py-2 px-2 top-0 right-0 rounded" onClick={() => setBggModalOpen(true)}>
                  Importa da BGG
                </button>
              : null
          }
        </div>
        <form className="w-full max-w-lg" onSubmit={handleSubmit}>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="title">
                Titolo
              </label>
              <Field
                id="title"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                name="title"
                type="text"
                component={renderField}
                placeholder="e.g. 7 Wonders"
                disabled={props.loading}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="description">
                Descrizione
              </label>
              <Field
                id="description"
                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                name="description"
                component={ReactMDE}
                buttonConfig={mdConfig}
                buttonStyle={mdButtonStyle}
                textAreaStyle={mdTextAreaStyle}
                placeholder="e.g. Un gioco straordinario di deduzione..."
                disabled={props.loading}
                validate={[required]} />
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="categories">
                Categorie
              </label>
              <div className="relative">
                <Field
                  id="categories"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="categories"
                  type="select"
                  multiple={true}
                  component={renderField}
                  disabled={props.loading}>
                    { props.categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>) }
                </Field>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="availableCopies">
                Numero di copie disponibili
              </label>
              <div className="relative">
                <Field
                  id="availableCopies"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="availableCopies"
                  type="select"
                  component={renderField}
                  disabled={props.loading}
                  normalize={toInt}
                  validate={[required]}>
                    { availableCopiesOptions.map(value => <option key={value} value={value}>{value}</option>) }
                </Field>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="minPlayers">
                Numero Minimo di Giocatori
              </label>
              <div className="relative">
                <Field
                  id="minPlayers"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="minPlayers"
                  type="select"
                  component={renderField}
                  disabled={props.loading}
                  normalize={toInt}
                  validate={[required]}>
                  { minPlayersOptions.map(value => <option key={value} value={value}>{value}</option>) }
                </Field>
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="maxPlayers">
                Numero Massimo di Giocatori
              </label>
              <div className="relative">
                <Field
                  id="maxPlayers"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="maxPlayers"
                  type="select"
                  component={renderField}
                  disabled={props.loading}
                  normalize={toInt}>
                  <option value="">N/A</option>
                  { maxPlayersOptions.map(value => <option key={value} value={value}>{value}</option>) }
                </Field>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="minPlayTime">
                Tempo minimo di gioco
              </label>
              <div className="relative">
                <Field
                  id="minPlayTime"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="minPlayTime"
                  type="select"
                  component={renderField}
                  disabled={props.loading}
                  normalize={toInt}
                  validate={[required]}>
                    { minPlayTimeOptions.map(value => <option key={value} value={value}>{value} min.</option>) }
                </Field>
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="maxPlayTime">
                Tempo massimo di gioco
              </label>
              <div className="relative">
              <Field
                  id="maxPlayTime"
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  name="maxPlayTime"
                  type="select"
                  component={renderField}
                  disabled={props.loading}
                  normalize={toInt}>
                    <option value="">N/A</option>
                    { maxPlayTimeOptions.map(value => <option key={value} value={value}>{value} min.</option>) }
                </Field>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 md:mb-2">
            <div className="w-full md:w-1/5 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="bbgId">
                BGG ID
              </label>
              <div className="relative flex">
                <Field
                  id="bggId"
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                  name="bggId"
                  type="text"
                  component={renderField}
                  placeholder="e.g. 01927834"
                  disabled={props.loading}
                  normalize={toInt}
                  validate={[required]} />
              </div>
            </div>
            <div className="w-full md:w-2/5 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="image">
                Immagine
              </label>
              <div className="relative">
                <Field
                  id="image"
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded rounded-r-none py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                  name="image"
                  type="text"
                  component={renderFileField}
                  handleFile={handleFile}
                  placeholder="e.g. https://site.com/image.png"
                  disabled={props.loading || isUploading}
                  validate={[required, url]} />
              </div>
            </div>
            <div className="w-full md:w-2/5 px-3 mb-6 md:mb-2">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="thumbnail">
                Thumbnail
              </label>
              <div className="relative">
                <Field
                  id="thumbnail"
                  className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded rounded-r-none py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                  name="thumbnail"
                  type="text"
                  component={renderFileField}
                  handleFile={handleFile}
                  placeholder="e.g. https://site.com/thumbnail.png"
                  disabled={props.loading || isUploading}
                  validate={[required, url]} />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3 mb-8">
            <div className="w-full px-3 mb-6 md:mb-2">
              <FieldArray
                name="articles"
                template={{
                  label: 'Articoli',
                  fields: [
                    {
                      id: 'label',
                      name: 'label',
                      type: 'text',
                      className: 'md:flex-1 appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white',
                      placeholder: 'Titolo',
                      validate: [required],
                      wrapperClassName: 'md:flex-1 w-full mb-1 md:mb-0 md:pr-2 ',
                    },
                    {
                      id: 'url',
                      name: 'url',
                      type: 'text',
                      className: 'md:flex-2 appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 py-3 px-4 rounded leading-tight focus:outline-none focus:bg-white',
                      placeholder: 'URL',
                      validate: [required, url],
                      wrapperClassName: 'md:flex-2 w-full mb-1 md:mb-0 md:pl-2 md:pr-4'
                    },
                  ],
                  addButton: {
                    className: 'bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded',
                    text: 'Aggiungi',
                  },
                }}
                component={renderArray} />
            </div>
          </div>
          <div className="flex flex-wrap justify-center">
            <button disabled={invalid || submitting || isUploading} className={`w-full md:w-auto bg-blue-700 text-white font-bold py-4 px-4 rounded ${invalid || submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-800'}`} type="submit">
              Salva
            </button>
          </div>
        </form>
      </div>
      <BggModal isOpen={isBggModalOpen} onClose={() => setBggModalOpen(false)} onGameSelected={onBggGameSelected}></BggModal>
    </React.Fragment>
  );
};

BoardgameForm.propTypes = {
  title: PropTypes.string,
};

export default reduxForm({
  form: 'boardgame',
})(BoardgameForm);